<template>
    <NuxtLayout name="malt-base-layout">
        <main>
            <MobileTabList v-if="isSmall" :tabs="mobileTabs" />
            <VJoyAdminBanner v-if="isPlatformAdmin" />
            <VJoyTemplate sidebar="left">
                <template #template-title>
                    {{ t('client.settings.banner.title') }}
                </template>
                <template #template-subtitle>{{ t('client.settings.banner.subtitle') }}</template>
                <template #template-main>
                    <Transition name="fade" mode="out-in" v-on="routerEvents">
                        <KeepAlive>
                            <slot />
                        </KeepAlive>
                    </Transition>
                </template>
                <template #template-sidebar>
                    <TabList v-if="!isSmall" :tabs="tabs" />
                </template>
            </VJoyTemplate>
        </main>
        <NavbarLogged />
        <ClientOnboarding />
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
    import {useClientSettingsContextStore} from '@common/stores';
    import {useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {ClientOnboarding, NavbarLogged} from '#components';
    import {useAuth, useHead, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyAdminBanner, VJoyTemplate} from '@maltjoy/core-vue';
    import {ClientCompanyWizardStepStateResource} from '@api';
    import {eventBus} from '~/common/notifications/events';
    import {usePreventRouterBlink, useWindowSize} from '@/common/composables';
    import {ROUTES_NAMES} from '@/common/routes';
    import type {TabItem, TabItemNotification} from '@/common/types';
    import {MobileTabList, TabList} from '@/common/ui/tabs';
    import {getCssVar, useGetCompanyId} from '@/common/utils';
    import {useProfileStore} from '@/features/profile/stores';

    const {clientSettingsContext, isImmersionMode} = storeToRefs(useClientSettingsContextStore());
    const {t} = useTranslation();
    const $route = useRoute();
    const {isSmall} = useWindowSize();

    const {routerEvents, routerViewRef} = usePreventRouterBlink();

    const redirectToLegalEntityWallet = useFeatureFlag('ee-team-wallet-redirects-to-legal-entity-wallet');
    const shouldEnforceImmersionMode = (path: string): boolean => {
        const noImmersionModePaths = ['/legal', '/project', '/profile'];
        const parts = path.split('/').filter((part) => part.length > 0);
        if (!parts.length) return true;
        return !noImmersionModePaths.includes(`/${parts[parts.length - 1]}`);
    };
    const authStore = useAuth();
    const {user} = storeToRefs(authStore);
    const isPlatformAdmin = computed(() => user.value?.platformAdmin);

    if (useGetCompanyId() && isImmersionMode.value && shouldEnforceImmersionMode($route.path)) {
        const profileStore = useProfileStore();
        await profileStore.fetchCompanyProfileData();
        const {profileData} = storeToRefs(profileStore);
        useHead({bodyAttrs: {class: 'immersion-mode', 'data-immersion-user': profileData.value?.name}});
    }

    watch(
        () => $route.path,
        () => {
            const headerHeight = parseInt(getCssVar('--header-height').replace(/px/i, ''));
            if (routerViewRef.value && routerViewRef.value.getBoundingClientRect().top < headerHeight + 20) {
                window.scrollTo({top: 0, behavior: 'smooth'});
            }
        },
    );

    const tabsNotifications = ref<TabItemNotification[]>([]);

    function getNotificationLabelByTabName(tabName: string) {
        const value = tabsNotifications.value.find((item) => item.name === tabName);
        return value ? value.notificationLabel : undefined;
    }

    function setNotificationLabelByTabName(tabsNotifications: TabItemNotification[], tabName: string, notificationLabel: string | undefined) {
        const item = tabsNotifications.find((obj) => obj.name === tabName);

        if (item) {
            item.notificationLabel = notificationLabel;
        } else {
            tabsNotifications.push({name: tabName, notificationLabel});
        }
    }

    const notificationAdded = (payload: any) => {
        const notifications: TabItemNotification[] = tabsNotifications.value;
        setNotificationLabelByTabName(notifications, payload.tabName, payload.notificationLabel);
        tabsNotifications.value = notifications;
    };

    const notificationRemoved = (payload: any) => {
        const notifications: TabItemNotification[] = tabsNotifications.value;
        setNotificationLabelByTabName(notifications, payload.tabName, undefined);
        tabsNotifications.value = notifications;
    };

    onMounted(() => {
        eventBus.on('tab-notification-added', notificationAdded);
        eventBus.on('tab-notification-removed', notificationRemoved);
    });
    onBeforeUnmount(() => {
        eventBus.off('tab-notification-added', notificationAdded);
        eventBus.off('tab-notification-removed', notificationRemoved);
    });

    const tabs = computed((): TabItem[] =>
        [
            {
                name: 'profile',
                state: clientSettingsContext.value?.profileStepState,
                icon: 'company-placeholder',
                title: t('client.settings.profile.title'),
                to: {name: ROUTES_NAMES.profile},
                notificationLabel: getNotificationLabelByTabName('profile'),
            },
            {
                name: 'legal',
                state: clientSettingsContext.value?.legalStepState,
                icon: 'receipt',
                title: t('client.settings.legal.details.title'),
                to: {name: ROUTES_NAMES.legal},
                notificationLabel: getNotificationLabelByTabName('legal'),
            },
            {
                name: 'payment-preferences',
                state: clientSettingsContext.value?.paymentPreferencesStepState,
                icon: 'bank',
                title: t('client.settings.currency.title'),
                to: {name: ROUTES_NAMES.paymentPreferences},
                notificationLabel: getNotificationLabelByTabName('payment-preferences'),
            },
            {
                name: 'sso',
                state: clientSettingsContext.value?.ssoStepState,
                icon: 'key',
                title: t('client.settings.sso.title'),
                to: {name: ROUTES_NAMES.sso},
                notificationLabel: getNotificationLabelByTabName('sso'),
            },
            {
                name: 'wallet',
                state: clientSettingsContext.value?.walletStepState,
                icon: 'wallet',
                title: t('client.settings.wallet.title'),
                to: redirectToLegalEntityWallet.value
                    ? // FIXME -  this should be the current company's default LE  read from the clientSettingsContext
                      `../legal-entity/${useGetCompanyId()}/banking-details`
                    : {name: ROUTES_NAMES.wallet},
                notificationLabel: getNotificationLabelByTabName('wallet'),
            },
            {
                name: 'proposal',
                state: clientSettingsContext.value?.proposalSettingsStepSate,
                icon: 'file-check',
                title: t('client.settings.proposal.title'),
                to: {name: ROUTES_NAMES.proposal},
                notificationLabel: getNotificationLabelByTabName('proposal'),
            },
            {
                name: 'project',
                state: clientSettingsContext.value?.projectSettingsStepState,
                icon: 'file-check',
                title: t('client.settings.project.title'),
                to: {name: ROUTES_NAMES.project},
                notificationLabel: getNotificationLabelByTabName('project'),
            },
        ].filter((tab) => tab.state === ClientCompanyWizardStepStateResource.Valid || tab.state === ClientCompanyWizardStepStateResource.Invalid),
    );

    const mobileTabs = computed((): TabItem[] => tabs.value.filter((tab) => tab.condition === undefined || tab.condition));
</script>
